import {
  AppstoreOutlined,
  DashboardOutlined,
  DollarOutlined,
  EditOutlined,
  FolderOpenOutlined,
  LoadingOutlined,
  LogoutOutlined,
  MoonOutlined,
  RightOutlined,
  RocketOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { css } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { App, Avatar, Dropdown, Flex, MenuProps, Spin, Typography } from 'antd';

import { isNil } from 'lodash';

import { useMemo } from 'react';

import { useAccount } from '@lib/api/hooks/auth/use-account';

import { useLogout } from '@lib/api/hooks/auth/use-logout';
import { myStoreOptions } from '@lib/api/options/stores';
import { accountIsAdmin } from '@lib/types';
import { resolveProfilePhotoUrl } from '@lib/utils/profile-photo';
import { ROUTE_PATHS } from '@modules/route-paths';

import NewFeatureRow from './components/new-feature-row';
import { APP_THEME_MODE } from '../../../../config/theme';
import { useAppTheme } from '../../../../config/theme/provider';

interface Props {
  hideName?: boolean;
}

const styles = {
  menu: css`
    &.ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item {
      border-radius: 10px;
    }

    .ant-dropdown-menu-item-group-title {
      color: var(--ant-color-text-secondary);
      font-size: 10px;
      padding-bottom: 4px !important;
    }
  `,
  trigger: css`
    cursor: pointer;
    margin-left: var(--ant-margin-xs);
  `,
  avatar: css`
    flex: 0 0 auto;
  `,
  account: css`
    min-width: 220px;
    padding-block: 6px;
  `,
  accountName: css`
    max-width: 170px;
  `,
  openStoreItem: css`
    background-color: var(--ant-color-primary-bg);
  `,
  newItem: css`
    background-color: var(--ant-color-primary-bg);
  `,
};

const UserDropdown = ({ hideName = false }: Props) => {
  const { modal } = App.useApp();
  const account = useAccount();
  const { data: store } = useQuery({
    ...myStoreOptions(account?.isStoreOwner),
    enabled:
      !isNil(account) &&
      Boolean(account.isStoreOwner) &&
      !isNil(account.verifiedAt) &&
      !accountIsAdmin(account),
  });

  const { logout, logingOut } = useLogout();
  const { mode, toggleTheme } = useAppTheme();

  const isDarkMode = mode === APP_THEME_MODE.DARK;
  const themeActionLabel = isDarkMode
    ? 'Ativar tema claro'
    : 'Ativar tema escuro';

  const handleLogout = () => {
    modal.confirm({
      title: 'Tem certeza que deseja sair da plataforma?',
      onOk: () => logout(),
    });
  };

  const adminItems: MenuProps['items'] = useMemo(() => {
    if (account && accountIsAdmin(account)) {
      return [
        {
          key: 'admin-dashboard',
          label: <Link to={ROUTE_PATHS.ADMIN_HOME}>Painel Administrativo</Link>,
          icon: <DashboardOutlined />,
        },
      ];
    }

    return [];
  }, [account]);

  const storeItems: MenuProps['items'] = useMemo(() => {
    if (account && !account.isStoreOwner) {
      if (!accountIsAdmin(account)) {
        return [
          {
            key: 'open-store',
            label: <Link to={ROUTE_PATHS.SELLER}>Abrir minha loja</Link>,
            icon: <ShopOutlined />,
            className: styles.openStoreItem,
          },
        ];
      }

      return [];
    }

    return [
      {
        key: 'store-settings',
        label: <Link to={ROUTE_PATHS.SELLER}>Configurações</Link>,
        icon: <SettingOutlined />,
      },
      {
        key: 'store-inventory',
        label: (
          <Link to={ROUTE_PATHS.STORE_FOLDER} params={{ slug: store?.slug }}>
            Minha pasta
          </Link>
        ),
        icon: <FolderOpenOutlined />,
      },
      {
        key: 'store-local',
        label: (
          <Link to={ROUTE_PATHS.LOCAL_OWNER}>
            <NewFeatureRow>Nexo Local</NewFeatureRow>
          </Link>
        ),
        className: styles.newItem,
        icon: <ShopOutlined />,
      },
      {
        key: 'store-sales',
        label: <Link to={ROUTE_PATHS.STORE_SALES}>Minhas vendas</Link>,
        icon: <DollarOutlined />,
      },
    ];
  }, [account, store]);

  const avatarUrl = resolveProfilePhotoUrl(account?.profilePhotoUrl ?? null);
  const accountName =
    account?.name?.split(' ').slice(0, 2).join(' ') || 'Usuário';

  const accountInitial = accountName.trim().charAt(0).toUpperCase();
  const items: MenuProps['items'] = [
    {
      key: 'account-summary',
      label: (
        <Link to={ROUTE_PATHS.PROFILE}>
          <Flex className={styles.account} align="center" gap={12}>
            <Avatar src={avatarUrl}>{accountInitial}</Avatar>
            <Flex vertical>
              <Typography.Text className={styles.accountName} ellipsis strong>
                {accountName}
              </Typography.Text>
              <Typography.Text type="secondary">
                {account?.email}
              </Typography.Text>
            </Flex>
            <RightOutlined />
          </Flex>
        </Link>
      ),
    },
    { type: 'divider' },
    ...(adminItems.length > 0
      ? [
          {
            key: 'admin',
            type: 'group' as const,
            label: 'ADMINISTRAÇÃO',
            children: adminItems,
          },
        ]
      : []),
    {
      key: 'my-account',
      type: 'group',
      label: 'MINHA CONTA',
      children: [
        {
          key: 'profile',
          label: <Link to={ROUTE_PATHS.PROFILE}>Atualizar perfil</Link>,
          icon: <EditOutlined />,
        },
        account?.isStoreOwner
          ? {
              key: 'wallet',
              label: <Link to={ROUTE_PATHS.STORE_FINANCE}>Minha carteira</Link>,
              icon: <DollarOutlined />,
            }
          : null,
      ],
    },
    ...(storeItems.length
      ? [
          {
            key: 'store',
            label: 'MINHA LOJA',
            type: 'group' as const,
            children: storeItems,
          },
        ]
      : []),
    {
      key: 'buy',
      type: 'group',
      label: 'COMPRAS E CARTAS',
      children: [
        {
          key: 'purchases',
          label: <Link to={ROUTE_PATHS.PURCHASES}>Minhas compras</Link>,
          icon: <ShoppingOutlined />,
        },
        {
          key: 'decks',
          className: styles.newItem,
          label: (
            <Link to={ROUTE_PATHS.MY_DECKS}>
              <NewFeatureRow>Meus decks</NewFeatureRow>
            </Link>
          ),
          icon: <AppstoreOutlined />,
        },
        {
          key: 'optimized-purchase',
          className: styles.newItem,
          label: (
            <Link to={ROUTE_PATHS.OPTIMIZED_PURCHASE}>
              <NewFeatureRow>Compra otimizada</NewFeatureRow>
            </Link>
          ),
          icon: <RocketOutlined />,
        },
      ],
    },
    { type: 'divider' },
    {
      key: 'theme',
      label: themeActionLabel,
      icon: isDarkMode ? <SunOutlined /> : <MoonOutlined />,
      onClick: toggleTheme,
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: logingOut ? (
        <Spin indicator={<LoadingOutlined spin />} size="small" />
      ) : (
        <LogoutOutlined />
      ),
      onClick: handleLogout,
      danger: true,
    },
  ];

  return (
    <Dropdown
      rootClassName={styles.menu}
      menu={{ items }}
      placement="bottomRight"
      trigger={['click']}
    >
      <Flex className={styles.trigger} align="center" gap={8}>
        <Avatar
          aria-label={`Abrir opções de ${accountName}`}
          className={styles.avatar}
          size={34}
          src={avatarUrl}
          alt={accountName}
        >
          {accountInitial}
        </Avatar>
        {hideName ? null : (
          <Typography.Text ellipsis>{accountName}</Typography.Text>
        )}
      </Flex>
    </Dropdown>
  );
};

export default UserDropdown;
